import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  isAuthenticated,
  getSub,
  AUTH_SESSION_TOKENS_UPDATED_EVENT,
} from "../services/api";
import userService from "../services/userService";
import * as ticketService from "../services/ticketService";
import { setAppOriginRegistry } from "../services/appOriginService";
import { getActiveAppOrigins } from "../services/appOriginDefinitionService";
import { setResolutionTypeRegistry } from "../services/resolutionTypeService";
import { getActiveResolutionTypes } from "../services/resolutionTypeDefinitionService";
import { getCompanyNames } from "../services/companyService";
import { getActiveTicketStatuses } from "../services/ticketStatusService";
import { getActiveDemandTypes } from "../services/demandTypeDefinitionService";
import { getStatusLabel, setStatusRegistry, isTerminalStatus, requiresResolutionCapture } from "../services/statusService";
import { setDemandTypeRegistry } from "../services/demandTypeService";
import {
  getFilterPresets,
  getDefaultFilterPreset,
  createFilterPreset,
  setDefaultFilterPreset,
  deleteFilterPreset,
} from "../services/filterPresetService";

const defaultContextValue = {
  toasts: [],
  removeToast: () => {},
};

export const AppContext = createContext(defaultContextValue);

export const useApp = () => useContext(AppContext);

const isAbortError = (err) =>
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError';

const UNASSIGNED_RESPONSIBLE_FILTER = 'UNASSIGNED';
const TICKET_SCAN_PAGE_SIZE = 100;
const MAX_TICKET_SCAN_PAGES = 30;
const USER_LIST_VIEW_MODE_KEY = 'supporthub.userTickets.viewMode';

const isUnassignedResponsibleValue = (value) => {
  const normalized = String(value ?? '').trim();
  return (
    normalized.toUpperCase() === UNASSIGNED_RESPONSIBLE_FILTER ||
    normalized.toLocaleLowerCase('pt-BR') === 'ninguém'
  );
};

const splitResponsibleFilter = (values) => {
  const list = Array.isArray(values) ? values : [];
  return {
    includeUnassigned: list.some(isUnassignedResponsibleValue),
    emails: list.filter((value) => value && !isUnassignedResponsibleValue(value)),
  };
};

const ticketResponsibleEmails = (ticket) => {
  const fromUsers = Array.isArray(ticket?.responsibleUsers)
    ? ticket.responsibleUsers.map((user) => user?.email)
    : [];
  const fromEmails = Array.isArray(ticket?.responsibleUserEmails) ? ticket.responsibleUserEmails : [];
  return [...fromUsers, ...fromEmails]
    .map((email) => String(email ?? '').trim().toLowerCase())
    .filter(Boolean);
};

const ticketMatchesResponsibleSelection = (ticket, includeUnassigned, emails) => {
  if (includeUnassigned && ticketResponsibleEmails(ticket).length === 0) return true;
  if (!emails.length) return false;
  const wanted = new Set(emails.map((email) => String(email).trim().toLowerCase()));
  return ticketResponsibleEmails(ticket).some((email) => wanted.has(email));
};

/** Status aplicados por default em "Meus Tickets" (o utilizador pode alterar e carregar em Buscar). */
const DEFAULT_USER_TICKET_STATUSES = Object.freeze([
  'OPEN',
  'IN_PROGRESS',
  'PENDING',
]);

export const AppProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setErrorState] = useState(null);
  const setError = (value) => {
    if (value === 'Session ending') return;
    setErrorState(value);
  };
  const [detailsError, setDetailsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0); // base 0
  const [hasMore, setHasMore] = useState(true);
  const [totalTickets, setTotalTickets] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(() => []);
  const [listViewMode, setListViewMode] = useState('cards');
  const [currentViewKey, setCurrentViewKey] = useState('all-tickets');
  const [kanbanTickets, setKanbanTickets] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [filterPresets, setFilterPresets] = useState([]);
  const [activeFilterPresetId, setActiveFilterPresetId] = useState('');
  const viewConfigSeqRef = useRef(0);
  const initializedViewKeysRef = useRef(new Set());
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentStatus, setCurrentStatus] = useState("");
  const authRef = useRef(isAuthenticated());
  const initialFetchDone = useRef(false);
  const fetchTimeout = useRef(null);
  const intervalRef = useRef(null);
  const mountRef = useRef(false);
  const fetchingRef = useRef(false);
  const ticketFetchingRef = useRef(false);
  const ticketsListAbortRef = useRef(null);
  const userTicketsAbortRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserViewModeRef = useRef(null);
  const [selectedPriority, setSelectedPriority] = useState([]);
  const [selectedAppOrigin, setSelectedAppOrigin] = useState([]);
  const [appOrigins, setAppOrigins] = useState([]);
  const [loadingAppOrigins, setLoadingAppOrigins] = useState(false);
  const [resolutionTypes, setResolutionTypes] = useState([]);
  const [loadingResolutionTypes, setLoadingResolutionTypes] = useState(false);
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  const [toasts, setToasts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [selectedDemandType, setSelectedDemandType] = useState([]);
  const [viewDemandTypePreset, setViewDemandTypePreset] = useState([]);
  const [selectedCompanyNames, setSelectedCompanyNames] = useState([]);
  const [selectedResponsibleEmails, setSelectedResponsibleEmails] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [loadingTicketStatuses, setLoadingTicketStatuses] = useState(false);
  const [demandTypes, setDemandTypes] = useState([]);
  const [loadingDemandTypes, setLoadingDemandTypes] = useState(false);
  const [userTickets, setUserTickets] = useState([]);
  const [userTotalElements, setUserTotalElements] = useState(0);
  const [userTotalPages, setUserTotalPages] = useState(0);
  const [userCurrentPage, setUserCurrentPage] = useState(0);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);
  const [userSelectedStatus, setUserSelectedStatus] = useState(() => [
    ...DEFAULT_USER_TICKET_STATUSES,
  ]);
  const [userSelectedPriority, setUserSelectedPriority] = useState([]);
  const [userListViewMode, setUserListViewModeState] = useState(() => {
    try {
      return localStorage.getItem(USER_LIST_VIEW_MODE_KEY) === 'kanban' ? 'kanban' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const [userKanbanTickets, setUserKanbanTickets] = useState([]);
  const [userKanbanLoading, setUserKanbanLoading] = useState(false);

  console.log('AppProvider initialized with auth status:', isAuthenticated());

  // Debug effect para monitorar mudanças nos usuários
  useEffect(() => {
    console.log('AppContext - users state changed:', users?.length || 0);
  }, [users]);

  // Debug effect para monitorar mudanças nas origens dos apps
  useEffect(() => {
    console.log('AppContext - appOrigins state changed:', appOrigins?.length || 0);
  }, [appOrigins]);

  const fetchUsers = useCallback(async () => {
    // Verificar autenticação em tempo real ao invés de usar authRef.current
    if (!isAuthenticated()) {
      console.log("Not authenticated, skipping users fetch");
      return;
    }

    try {
      console.log("Fetching users...");
      setLoadingUsers(true);
      const response = await userService.getAssignable();
      console.log("Users fetched successfully:", response);
      console.log("Users response type:", typeof response);
      console.log("Users response is array:", Array.isArray(response));
      console.log("Users content:", response.content);
      console.log("Users content type:", typeof response.content);
      console.log("Users content is array:", Array.isArray(response.content));
      console.log("Users array length:", response.content?.length || 0);

      const usersArray = Array.isArray(response) ? response : [];

      console.log("Final users array:", usersArray);
      console.log("Final users array length:", usersArray.length);
      setUsers(usersArray);
    } catch (err) {
      console.error("Error fetching users:", err);
      if (err?.message !== 'Session ending') {
        setError(err.message);
      }
      throw err;
    } finally {
      setLoadingUsers(false);
    }
  }, []);


  const fetchCurrentUser = useCallback(async () => {
    // Verificar autenticação em tempo real ao invés de usar authRef.current
    if (!isAuthenticated()) {
      console.log("Not authenticated, skipping current user fetch");
      return;
    }

    try {
      console.log("Fetching current user...");
      const response = await userService.getCurrentUser();
      console.log("Current user fetched successfully:", response);
      setCurrentUser(response);
      currentUserViewModeRef.current = response?.ticketListViewMode ?? null;
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  }, []);

  // Função para forçar recarregamento dos usuários
  const reloadUsers = useCallback(async () => {
    console.log("Forcing users reload...");
    try {
      await fetchUsers();
    } catch (error) {
      console.error("Error reloading users:", error);
    }
  }, [fetchUsers]);

  const reloadAppOrigins = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      setLoadingAppOrigins(true);
      const list = await getActiveAppOrigins();
      const normalized = Array.isArray(list) ? list : [];
      setAppOrigins(normalized);
      setAppOriginRegistry(normalized);
    } catch (err) {
      console.error("Error fetching app origins:", err);
      setAppOrigins([]);
      setAppOriginRegistry(null);
    } finally {
      setLoadingAppOrigins(false);
    }
  }, []);

  const reloadResolutionTypes = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      setLoadingResolutionTypes(true);
      const list = await getActiveResolutionTypes();
      const normalized = Array.isArray(list) ? list : [];
      setResolutionTypes(normalized);
      setResolutionTypeRegistry(normalized);
    } catch (err) {
      console.error("Error fetching resolution types:", err);
      setResolutionTypes([]);
      setResolutionTypeRegistry(null);
    } finally {
      setLoadingResolutionTypes(false);
    }
  }, []);

  // Initial data fetching
  useEffect(() => {
    const fetchData = async () => {
      // Verificar autenticação em tempo real
      const isAuth = isAuthenticated();
      console.log("Initial data fetch - auth status:", isAuth);
      console.log("Initial data fetch - initialFetchDone:", initialFetchDone.current);
      console.log("Initial data fetch - mountRef:", mountRef.current);
      console.log("Initial data fetch - fetchingRef:", fetchingRef.current);

      if (
        !isAuth ||
        mountRef.current ||
        fetchingRef.current
      ) {
        console.log(
          "Skipping data fetch - not authenticated, already mounted, or currently fetching"
        );
        return;
      }

      console.log("Starting initial data fetch");
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        await Promise.all([fetchUsers(), fetchCurrentUser()]);
        initialFetchDone.current = true;
        mountRef.current = true;
      } catch (error) {
        console.error("Error in initial data fetch:", error);
        setError(error.message);
        if (fetchTimeout.current) {
          clearTimeout(fetchTimeout.current);
        }
        fetchTimeout.current = setTimeout(() => {
          initialFetchDone.current = false;
          mountRef.current = false;
          ticketFetchingRef.current = false;
        }, 5000);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchData();

    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [fetchUsers, fetchCurrentUser]);

  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      console.log("Auth check - current status:", authStatus);
      console.log("Auth check - previous isAuth state:", isAuth);
      setIsAuth(authStatus);

      // Se acabou de autenticar e ainda não carregou os dados iniciais
      if (authStatus && !initialFetchDone.current && !fetchingRef.current) {
        console.log("User just authenticated, triggering initial data fetch");
        fetchUsers();
        fetchCurrentUser();
        initialFetchDone.current = true;
        mountRef.current = true;
      }

      // Se acabou de autenticar mas já tinha carregado dados antes (login sem reload)
      if (authStatus && initialFetchDone.current && !fetchingRef.current) {
        console.log("User authenticated but data might be stale, reloading users");
        console.log("Current users count:", users.length);
        fetchUsers();
        fetchCurrentUser();
      }

      // Se está autenticado mas não há usuários carregados
      if (authStatus && users.length === 0 && !fetchingRef.current) {
        console.log("User authenticated but no users loaded, fetching users");
        fetchUsers();
      }

      if (!authStatus) {
        console.log("User not authenticated, clearing sensitive data");
        // Limpe estados sensíveis se necessário
        setCurrentUser(null);
        currentUserViewModeRef.current = null;
        setTickets([]);
        setSelectedTicket(null);
        setTicketDetails(null);
        setError(null);
        setDetailsError(null);
        setUsers([]);
        setAppOrigins([]);
        initialFetchDone.current = false;
        mountRef.current = false;
        // Redirecione para login se desejar (ex: navigate('/login'))
      }
    };

    // Verificar imediatamente
    checkAuth();

    // Removido o polling de 5 segundos para evitar re-renderizações globais desnecessárias
    // Se desejar polling apenas do status de autenticação, pode-se adicionar um setInterval aqui que só chama setIsAuth(isAuthenticated())
    // Mas não deve chamar fetchUsers/fetchCurrentUser periodicamente
    return undefined;
  }, [fetchUsers, fetchCurrentUser, users.length, appOrigins.length]);

  // Garante fetch inicial após login, mesmo sem reload
  useEffect(() => {
    if (isAuth && !initialFetchDone.current && !fetchingRef.current) {
      console.log('Detectado login - disparando fetch inicial de usuários e usuário atual');
      fetchingRef.current = true;
      Promise.all([fetchUsers(), fetchCurrentUser()])
        .then(() => {
          initialFetchDone.current = true;
          mountRef.current = true;
        })
        .catch((error) => {
          if (error?.message !== 'Session ending') {
            setError(error.message);
          }
        })
        .finally(() => {
          fetchingRef.current = false;
        });
    }
  }, [isAuth, fetchUsers, fetchCurrentUser]);

  // Polling leve para atualizar o estado de autenticação
  useEffect(() => {
    const interval = setInterval(() => {
      const authStatus = isAuthenticated();
      setIsAuth(authStatus);
    }, 1000); // 1 segundo
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const syncAuthFromTokens = () => setIsAuth(isAuthenticated());
    window.addEventListener(AUTH_SESSION_TOKENS_UPDATED_EVENT, syncAuthFromTokens);
    return () =>
      window.removeEventListener(AUTH_SESSION_TOKENS_UPDATED_EVENT, syncAuthFromTokens);
  }, []);

  const createTicket = useCallback(async (ticketData) => {
    if (!isAuthenticated()) return;

    try {
      setLoading(true);
      const response = await ticketService.createTicket(ticketData);
      setTickets((prev) => [...prev, response]);
      setError(null);
      return response;
    } catch (err) {
      console.error("Error creating ticket:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTicket = useCallback(
    async (ticketId, ticketData) => {
      if (!isAuthenticated()) return;

      try {
        setLoading(true);
        const response = await ticketService.updateTicket(ticketId, ticketData);
        setTickets((prev) =>
          prev.map((ticket) => (ticket.id === ticketId ? response : ticket))
        );
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(response);
          setTicketDetails(response);
        }
        setError(null);
        return response;
      } catch (err) {
        console.error("Error updating ticket:", err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [selectedTicket]
  );

  const deleteTicket = useCallback(
    async (ticketId) => {
      if (!isAuthenticated()) return;

      try {
        setLoading(true);
        await ticketService.deleteTicket(ticketId);
        setTickets((prev) => prev.filter((ticket) => ticket.id !== ticketId));
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(null);
          setTicketDetails(null);
        }
        setError(null);
      } catch (err) {
        console.error("Error deleting ticket:", err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [selectedTicket]
  );

  const createUser = useCallback(async (userData) => {
    if (!isAuthenticated()) return;

    try {
      setLoading(true);
      const response = await userService.create(userData);
      setUsers((prev) => [...prev, response]);
      setError(null);
      return response;
    } catch (err) {
      console.error("Error creating user:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (userId, userData) => {
    if (!isAuthenticated()) return;

    try {
      setLoading(true);
      const response = await userService.update(userId, userData);
      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? response : user))
      );
      setError(null);
      return response;
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteUser = useCallback(async (userId) => {
    if (!isAuthenticated()) return;

    try {
      setLoading(true);
      await userService.delete(userId);
      setUsers((prev) => prev.filter((user) => user.id !== userId));
      setError(null);
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStatusChange = useCallback((statusList) => {
    const normalizedStatus = Array.isArray(statusList)
      ? [...statusList]
      : statusList
        ? [statusList]
        : [];
    setSelectedStatus(normalizedStatus);
    setCurrentPage(0);
  }, []);

  const handleUserStatusChange = useCallback((statusList) => {
    const normalizedStatus = Array.isArray(statusList)
      ? [...statusList]
      : statusList
        ? [statusList]
        : [];
    setUserSelectedStatus(normalizedStatus);
    setUserCurrentPage(0);
  }, []);

  const handleUserPriorityChange = useCallback((priorityList) => {
    const normalizedPriority = Array.isArray(priorityList)
      ? [...priorityList]
      : priorityList
        ? [priorityList]
        : [];
    setUserSelectedPriority(normalizedPriority);
    setUserCurrentPage(0);
  }, []);

  const handlePriorityChange = useCallback((priorityList) => {
    const normalizedPriority = Array.isArray(priorityList)
      ? [...priorityList]
      : priorityList
        ? [priorityList]
        : [];
    setSelectedPriority(normalizedPriority);
    setCurrentPage(0); // base 0
  }, []);

  const handleAppOriginChange = useCallback((appOriginList) => {
    const normalizedAppOrigin = Array.isArray(appOriginList)
      ? [...appOriginList]
      : appOriginList
        ? [appOriginList]
        : [];
    setSelectedAppOrigin(normalizedAppOrigin);
    setCurrentPage(0); // base 0
  }, []);

  const handleSearchTextChange = useCallback((text) => {
    setSearchText(text);
    setCurrentPage(0);
  }, []);

  const handleDemandTypeChange = useCallback((demandTypeList) => {
    const normalized = Array.isArray(demandTypeList)
      ? [...demandTypeList]
      : demandTypeList
        ? [demandTypeList]
        : [];
    setSelectedDemandType(normalized);
    setCurrentPage(0);
  }, []);

  const applyViewDemandTypePreset = useCallback((preset) => {
    const normalized = Array.isArray(preset) ? [...preset] : preset ? [preset] : [];
    setViewDemandTypePreset(normalized);
    setSelectedDemandType(normalized);
    setCurrentPage(0);
  }, []);

  const resolvePreferredViewMode = useCallback(async () => {
    const cached = currentUserViewModeRef.current;
    if (cached === 'kanban') return 'kanban';
    if (cached === 'cards') return 'cards';
    if (!isAuthenticated()) return 'cards';
    try {
      const user = await userService.getCurrentUser();
      setCurrentUser(user);
      currentUserViewModeRef.current = user?.ticketListViewMode ?? null;
      return user?.ticketListViewMode === 'kanban' ? 'kanban' : 'cards';
    } catch (err) {
      console.error('Error resolving preferred ticket list view mode:', err);
      return 'cards';
    }
  }, []);

  const resolveViewModeFromFilters = useCallback((filtersViewMode, preferredViewMode) => {
    if (preferredViewMode === 'kanban' || preferredViewMode === 'cards') {
      return preferredViewMode;
    }
    if (filtersViewMode === 'kanban' || filtersViewMode === 'cards') {
      return filtersViewMode;
    }
    return 'cards';
  }, []);

  const applyFiltersSnapshot = useCallback((filters, lockedDemandType = [], preferredViewMode = 'cards') => {
    const f = filters || {};
    setSelectedStatus(Array.isArray(f.status) ? [...f.status] : []);
    setSelectedPriority(Array.isArray(f.priority) ? [...f.priority] : []);
    setSelectedAppOrigin(Array.isArray(f.appOrigin) ? [...f.appOrigin] : []);
    if (!lockedDemandType?.length) {
      setSelectedDemandType(Array.isArray(f.demandType) ? [...f.demandType] : []);
    }
    setSelectedCompanyNames(Array.isArray(f.companyNames) ? [...f.companyNames] : []);
    setSelectedResponsibleEmails(
      Array.isArray(f.responsibleEmails) ? [...f.responsibleEmails] : []
    );
    const text = f.searchText || '';
    setSearchText(text);
    setDebouncedSearchText(text);
    setListViewMode(resolveViewModeFromFilters(f.viewMode, preferredViewMode));
    setCurrentPage(0);
  }, [resolveViewModeFromFilters]);

  const getFiltersSnapshot = useCallback(() => ({
    status: selectedStatus,
    priority: selectedPriority,
    appOrigin: selectedAppOrigin,
    demandType: selectedDemandType,
    companyNames: selectedCompanyNames,
    responsibleEmails: selectedResponsibleEmails,
    searchText: debouncedSearchText,
    viewMode: listViewMode,
  }), [
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    selectedDemandType,
    selectedCompanyNames,
    selectedResponsibleEmails,
    debouncedSearchText,
    listViewMode,
  ]);

  const reloadFilterPresets = useCallback(async (viewKey) => {
    if (!isAuthenticated() || !viewKey) {
      setFilterPresets([]);
      return;
    }
    try {
      const list = await getFilterPresets(viewKey);
      setFilterPresets(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading filter presets:', err);
      setFilterPresets([]);
    }
  }, []);

  const applyViewConfig = useCallback(async ({ viewKey, demandTypePreset, defaultStatus }) => {
    setCurrentViewKey(viewKey);

    if (initializedViewKeysRef.current.has(viewKey)) {
      const normalizedPreset = Array.isArray(demandTypePreset) ? [...demandTypePreset] : [];
      setViewDemandTypePreset(normalizedPreset);

      if (normalizedPreset.length > 0) {
        setSelectedDemandType(normalizedPreset);
      }

      await reloadFilterPresets(viewKey);
      return;
    }

    initializedViewKeysRef.current.add(viewKey);

    const seq = ++viewConfigSeqRef.current;
    setViewDemandTypePreset(demandTypePreset);
    setSelectedDemandType(demandTypePreset);
    setActiveFilterPresetId('');
    setCurrentPage(0);

    const preferredViewMode = await resolvePreferredViewMode();

    await reloadFilterPresets(viewKey);

    let appliedSaved = false;
    if (isAuthenticated()) {
      try {
        const saved = await getDefaultFilterPreset(viewKey);
        if (seq === viewConfigSeqRef.current && saved?.filters) {
          applyFiltersSnapshot(saved.filters, demandTypePreset, preferredViewMode);
          setActiveFilterPresetId(saved.id || '');
          appliedSaved = true;
        }
      } catch (err) {
        if (err?.response?.status !== 204) {
          console.error('Error loading default filter preset:', err);
        }
      }
    }

    if (seq === viewConfigSeqRef.current && !appliedSaved) {
      applyFiltersSnapshot(
        {
          status: defaultStatus || [],
          priority: [],
          appOrigin: [],
          demandType: demandTypePreset,
          companyNames: [],
          responsibleEmails: [],
          searchText: '',
          viewMode: preferredViewMode,
        },
        demandTypePreset,
        preferredViewMode
      );
    }
  }, [applyFiltersSnapshot, listViewMode, reloadFilterPresets, resolvePreferredViewMode]);

  const applyFilterPresetById = useCallback(
    (presetId) => {
      if (!presetId) {
        setActiveFilterPresetId('');
        return;
      }
      const preset = filterPresets.find((p) => p.id === presetId);
      if (!preset?.filters) return;
      applyFiltersSnapshot(preset.filters, viewDemandTypePreset, listViewMode);
      setActiveFilterPresetId(presetId);
    },
    [filterPresets, applyFiltersSnapshot, viewDemandTypePreset, listViewMode]
  );

  const saveCurrentFilterPreset = useCallback(
    async ({ name, defaultPreset }) => {
      const created = await createFilterPreset({
        viewKey: currentViewKey,
        name,
        defaultPreset: !!defaultPreset,
        filters: getFiltersSnapshot(),
      });
      await reloadFilterPresets(currentViewKey);
      setActiveFilterPresetId(created.id);
    },
    [currentViewKey, getFiltersSnapshot, reloadFilterPresets]
  );

  const makeFilterPresetDefault = useCallback(
    async (presetId) => {
      await setDefaultFilterPreset(presetId);
      await reloadFilterPresets(currentViewKey);
    },
    [currentViewKey, reloadFilterPresets]
  );

  const removeFilterPreset = useCallback(
    async (presetId) => {
      await deleteFilterPreset(presetId);
      if (activeFilterPresetId === presetId) {
        setActiveFilterPresetId('');
      }
      await reloadFilterPresets(currentViewKey);
    },
    [activeFilterPresetId, currentViewKey, reloadFilterPresets]
  );

  const setListViewModeAndRefresh = useCallback(async (mode) => {
    const normalized = mode === 'kanban' ? 'kanban' : 'cards';
    setListViewMode(normalized);
    setCurrentPage(0);
    currentUserViewModeRef.current = normalized;
    setCurrentUser((prev) =>
      prev ? { ...prev, ticketListViewMode: normalized } : prev
    );
    if (isAuthenticated()) {
      try {
        const updated = await userService.updateTicketListViewMode(normalized);
        if (updated?.ticketListViewMode) {
          currentUserViewModeRef.current = updated.ticketListViewMode;
          setCurrentUser((prev) =>
            prev ? { ...prev, ticketListViewMode: updated.ticketListViewMode } : prev
          );
        }
      } catch (err) {
        console.error('Error saving ticket list view mode:', err);
      }
    }
  }, []);

  const handleCompanyNamesChange = useCallback((names) => {
    const normalized = Array.isArray(names) ? [...names] : names ? [names] : [];
    setSelectedCompanyNames(normalized);
    setCurrentPage(0);
  }, []);

  const handleResponsibleEmailsChange = useCallback((emails) => {
    const normalized = Array.isArray(emails) ? [...emails] : emails ? [emails] : [];
    setSelectedResponsibleEmails(normalized);
    setCurrentPage(0);
  }, []);

  const reloadTicketStatuses = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      setLoadingTicketStatuses(true);
      const list = await getActiveTicketStatuses();
      const normalized = Array.isArray(list) ? list : [];
      setTicketStatuses(normalized);
      setStatusRegistry(normalized);
      return normalized;
    } catch (err) {
      console.error("Error fetching ticket statuses:", err);
      setTicketStatuses([]);
      setStatusRegistry(null);
    } finally {
      setLoadingTicketStatuses(false);
    }
  }, []);

  const reloadDemandTypes = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      setLoadingDemandTypes(true);
      const list = await getActiveDemandTypes();
      const normalized = Array.isArray(list) ? list : [];
      setDemandTypes(normalized);
      setDemandTypeRegistry(normalized);
    } catch (err) {
      console.error("Error fetching demand types:", err);
      setDemandTypes([]);
    } finally {
      setLoadingDemandTypes(false);
    }
  }, []);

  const fetchCompanyNames = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      setLoadingCompanyNames(true);
      const names = await getCompanyNames();
      setCompanyNames(Array.isArray(names) ? names : []);
    } catch (err) {
      console.error("Error fetching company names:", err);
      setCompanyNames([]);
    } finally {
      setLoadingCompanyNames(false);
    }
  }, []);

  const effectiveDemandTypeFilter = useCallback(() => {
    if (Array.isArray(viewDemandTypePreset) && viewDemandTypePreset.length > 0) {
      return viewDemandTypePreset;
    }
    return selectedDemandType;
  }, [viewDemandTypePreset, selectedDemandType]);

  const hasActiveFilters = useCallback(() => {
    return !!(
      (Array.isArray(selectedStatus) && selectedStatus.length > 0) ||
      (Array.isArray(selectedPriority) && selectedPriority.length > 0) ||
      (Array.isArray(selectedAppOrigin) && selectedAppOrigin.length > 0) ||
      (Array.isArray(selectedCompanyNames) && selectedCompanyNames.length > 0) ||
      (Array.isArray(selectedResponsibleEmails) && selectedResponsibleEmails.length > 0) ||
      (Array.isArray(selectedDemandType) && selectedDemandType.length > 0) ||
      (searchText && String(searchText).trim())
    );
  }, [
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    selectedCompanyNames,
    selectedResponsibleEmails,
    selectedDemandType,
    searchText,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchKanbanTickets = useCallback(async () => {
    if (!isAuth) return;

    ticketsListAbortRef.current?.abort();
    const controller = new AbortController();
    ticketsListAbortRef.current = controller;
    const { signal } = controller;

    setKanbanLoading(true);
    setLoading(true);
    setError(null);

    try {
      const demandTypeFilter = effectiveDemandTypeFilter();
      const statusParam =
        Array.isArray(selectedStatus) && selectedStatus.length > 0 ? selectedStatus : [];
      const { includeUnassigned, emails } = splitResponsibleFilter(selectedResponsibleEmails);
      const pageSizeKanban = TICKET_SCAN_PAGE_SIZE;
      let allTickets = [];
      let page = 0;
      let totalPagesApi = 1;

      while (page < totalPagesApi && page < MAX_TICKET_SCAN_PAGES) {
        const response = await ticketService.getTickets(
          page,
          statusParam,
          selectedPriority,
          pageSizeKanban,
          selectedAppOrigin,
          {
            demandType: demandTypeFilter,
            companyName: selectedCompanyNames,
            responsibleUserEmail: includeUnassigned ? [] : emails,
            searchText: debouncedSearchText,
          },
          { signal }
        );
        const content = response.content || [];
        allTickets = allTickets.concat(content);
        totalPagesApi = response.page?.totalPages ?? 1;
        page += 1;
      }

      if (includeUnassigned) {
        allTickets = allTickets.filter((ticket) =>
          ticketMatchesResponsibleSelection(ticket, true, emails)
        );
      }

      setKanbanTickets(allTickets);
      setTickets(allTickets);
      setTotalElements(allTickets.length);
      setTotalPages(1);
      setHasMore(false);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[fetchKanbanTickets] Erro:', err);
      setError(err.message);
    } finally {
      if (ticketsListAbortRef.current === controller) {
        setKanbanLoading(false);
        setLoading(false);
      }
    }
  }, [
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    selectedCompanyNames,
    selectedResponsibleEmails,
    debouncedSearchText,
    isAuth,
    effectiveDemandTypeFilter,
  ]);

  const fetchTickets = useCallback(async () => {
    if (!isAuth) return;

    ticketsListAbortRef.current?.abort();
    const controller = new AbortController();
    ticketsListAbortRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);

    try {
      const demandTypeFilter = effectiveDemandTypeFilter();
      const statusParam =
        Array.isArray(selectedStatus) && selectedStatus.length > 0 ? selectedStatus : [];
      const { includeUnassigned, emails } = splitResponsibleFilter(selectedResponsibleEmails);

      if (includeUnassigned) {
        let matched = [];
        let scanPage = 0;
        let totalPagesApi = 1;
        while (scanPage < totalPagesApi && scanPage < MAX_TICKET_SCAN_PAGES) {
          const response = await ticketService.getTickets(
            scanPage,
            statusParam,
            selectedPriority,
            TICKET_SCAN_PAGE_SIZE,
            selectedAppOrigin,
            {
              demandType: demandTypeFilter,
              companyName: selectedCompanyNames,
              responsibleUserEmail: [],
              searchText: debouncedSearchText,
            },
            { signal }
          );
          matched = matched.concat(response.content || []);
          totalPagesApi = response.page?.totalPages ?? 1;
          scanPage += 1;
        }
        matched = matched.filter((ticket) =>
          ticketMatchesResponsibleSelection(ticket, true, emails)
        );
        const start = currentPage * pageSize;
        const totalPages = matched.length === 0 ? 0 : Math.ceil(matched.length / pageSize);
        setTickets(matched.slice(start, start + pageSize));
        setTotalElements(matched.length);
        setTotalPages(totalPages);
        setHasMore(currentPage + 1 < totalPages);
        return;
      }

      const response = await ticketService.getTickets(
        currentPage,
        statusParam,
        selectedPriority,
        pageSize,
        selectedAppOrigin,
        {
          demandType: demandTypeFilter,
          companyName: selectedCompanyNames,
          responsibleUserEmail: emails,
          searchText: debouncedSearchText,
        },
        { signal }
      );

      const { content, page } = response;
      const { totalElements, totalPages, number: currentPageFromApi } = page || {};

      setTickets(content || []);
      setTotalElements(totalElements || 0);
      setTotalPages(totalPages || 0);

      if (currentPageFromApi !== undefined && currentPageFromApi !== currentPage) {
        setCurrentPage(currentPageFromApi);
      }
      setHasMore((currentPageFromApi || 0) + 1 < (totalPages || 0));
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      console.error("[fetchTickets] Erro ao buscar tickets:", err);
      setError(err.message);
    } finally {
      if (ticketsListAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [
    currentPage,
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    selectedCompanyNames,
    selectedResponsibleEmails,
    selectedDemandType,
    viewDemandTypePreset,
    debouncedSearchText,
    pageSize,
    isAuth,
    effectiveDemandTypeFilter,
  ]);

  const fetchUserTickets = useCallback(async () => {
    if (!isAuth) return;

    userTicketsAbortRef.current?.abort();
    const controller = new AbortController();
    userTicketsAbortRef.current = controller;
    const { signal } = controller;

    setUserLoading(true);
    setUserError(null);

    try {
      const sub = getSub();
      const response = await ticketService.getTicketsByResponsibleUser(
        sub,
        userCurrentPage,
        pageSize,
        userSelectedStatus,
        userSelectedPriority,
        [],
        { signal }
      );

      const { content, page } = response;
      const { totalElements, totalPages, number: currentPageFromApi } = page || {};

      setUserTickets(content || []);
      setUserTotalElements(totalElements || 0);
      setUserTotalPages(totalPages || 0);

      if (currentPageFromApi !== undefined && currentPageFromApi !== userCurrentPage) {
        setUserCurrentPage(currentPageFromApi);
      }
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      console.error("[fetchUserTickets] Erro ao buscar tickets do usuário:", err);
      setUserError(err.message);
    } finally {
      if (userTicketsAbortRef.current === controller) {
        setUserLoading(false);
      }
    }
  }, [userCurrentPage, pageSize, userSelectedStatus, userSelectedPriority, isAuth]);

  const fetchUserKanbanTickets = useCallback(async () => {
    if (!isAuth) return;

    userTicketsAbortRef.current?.abort();
    const controller = new AbortController();
    userTicketsAbortRef.current = controller;
    const { signal } = controller;

    setUserKanbanLoading(true);
    setUserError(null);

    try {
      const sub = getSub();
      let allTickets = [];
      let page = 0;
      let totalPagesApi = 1;

      while (page < totalPagesApi && page < MAX_TICKET_SCAN_PAGES) {
        const response = await ticketService.getTicketsByResponsibleUser(
          sub,
          page,
          TICKET_SCAN_PAGE_SIZE,
          userSelectedStatus,
          userSelectedPriority,
          [],
          { signal }
        );
        allTickets = allTickets.concat(response?.content || []);
        totalPagesApi = response?.page?.totalPages ?? 1;
        page += 1;
      }

      setUserKanbanTickets(allTickets);
      setUserTotalElements(allTickets.length);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[fetchUserKanbanTickets] Erro ao buscar tickets do usuário:', err);
      setUserError(err.message);
    } finally {
      if (userTicketsAbortRef.current === controller) {
        setUserKanbanLoading(false);
      }
    }
  }, [userSelectedStatus, userSelectedPriority, isAuth]);

  const setUserListViewMode = useCallback((mode) => {
    const next = mode === 'kanban' ? 'kanban' : 'cards';
    setUserListViewModeState(next);
    try {
      localStorage.setItem(USER_LIST_VIEW_MODE_KEY, next);
    } catch (err) {
      console.warn('[setUserListViewMode] Não foi possível salvar a preferência de visualização:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuth) return;
    if (window.location.pathname === '/dashboards/tv') return;
    if (listViewMode === 'kanban') {
      fetchKanbanTickets();
    } else {
      fetchTickets();
    }
  }, [
    isAuth,
    listViewMode,
    currentPage,
    pageSize,
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    selectedCompanyNames,
    selectedResponsibleEmails,
    selectedDemandType,
    viewDemandTypePreset,
    debouncedSearchText,
    fetchTickets,
    fetchKanbanTickets,
  ]);

  useEffect(() => {
    if (isAuth) {
      fetchCompanyNames();
      reloadTicketStatuses();
      reloadDemandTypes();
      reloadAppOrigins();
      reloadResolutionTypes();
    }
  }, [isAuth, fetchCompanyNames, reloadTicketStatuses, reloadDemandTypes, reloadAppOrigins, reloadResolutionTypes]);

  useEffect(() => {
    console.log("[AllTicketsList] Renderizou com:", {
      currentPage,
      totalPages,
      tickets
    });
  }, [currentPage, totalPages, tickets]);

  const fetchTicketDetails = useCallback(async (ticketId) => {
    if (!isAuthenticated() || !ticketId) return;

    try {
      setDetailsLoading(true);
      console.log("Fetching details for ticket:", ticketId);
      const response = await ticketService.getTicketById(ticketId);
      console.log("Fetched ticket details:", response);
      setTicketDetails(response);
      setDetailsError(null);
    } catch (err) {
      console.error("Error fetching ticket details:", err);
      setDetailsError(err.message);
      setTicketDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleSelectTicket = useCallback(
    async (ticket) => {
      console.log("Selecting ticket:", ticket);
      setSelectedTicket(ticket);
      setTicketDetails(null);
      setDetailsError(null);
      if (ticket?.id) {
        await fetchTicketDetails(ticket.id);
      }
    },
    [fetchTicketDetails]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      const pageIndex = newPage - 1; // base 1 para base 0
      console.log("[handlePageChange] Solicitando página:", newPage, "(índice base 0:", pageIndex, ")");
      if (pageIndex >= 0 && pageIndex < totalPages && pageIndex !== currentPage) {
        setCurrentPage(pageIndex);
      }
    },
    [currentPage, totalPages]
  );

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  }, []);

  const handleUserPageChange = useCallback((newPage) => {
    const pageIndex = newPage - 1;
    if (pageIndex >= 0 && pageIndex < userTotalPages && pageIndex !== userCurrentPage) {
      setUserCurrentPage(pageIndex);
    }
  }, [userCurrentPage, userTotalPages]);

  const handleUserPageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setUserCurrentPage(0);
  }, []);

  // Novo método centralizado para atualizar responsável(eis), prioridade e appOrigin
  const updateTicketFields = useCallback(async (ticketId, fields) => {
    return await ticketService.updateTicketFields(ticketId, fields);
  }, []);

  // Funções para gerenciar toasts
  const addToast = useCallback((message, type = 'info', options = {}) => {
    const id = Date.now() + Math.random();
    const action =
      options?.action?.label && options?.action?.to
        ? { label: options.action.label, to: options.action.to }
        : null;
    const newToast = { id, message, type, action };
    setToasts(prev => [...prev, newToast]);

    // Toasts com ação ficam mais tempo para dar chance de clicar
    setTimeout(() => {
      removeToast(id);
    }, action ? 8000 : 5000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const moveKanbanTicketStatus = useCallback(async (ticketId, newStatus, resolutionPayload = null) => {
    let previousStatus = null;
    let shouldUpdate = false;
    let ticketSnapshot = null;

    setKanbanTickets((prev) => {
      const ticket = prev.find((t) => t.id === ticketId);
      if (!ticket) return prev;
      if (String(ticket.status || '').toUpperCase() === String(newStatus).toUpperCase()) {
        return prev;
      }
      previousStatus = ticket.status;
      ticketSnapshot = ticket;
      shouldUpdate = true;
      return prev.map((t) =>
        t.id === ticketId ? { ...t, status: newStatus } : t
      );
    });

    if (!shouldUpdate) return;

    try {
      const needsResolution =
        resolutionPayload != null || requiresResolutionCapture(newStatus);

      if (isTerminalStatus(previousStatus)) {
        await ticketService.reopenTicket(ticketId, newStatus);
      } else if (needsResolution) {
        const ticket = ticketSnapshot;
        const requestPayload = {
          responsibleUsers: Array.isArray(ticket?.responsibleUsers)
            ? ticket.responsibleUsers
            : ticket?.responsibleUser
              ? [ticket.responsibleUser]
              : [],
          subjectDescription: ticket?.subjectDescription || ticket?.subject,
          detailedDescription: ticket?.detailedDescription ?? ticket?.description ?? '',
          attachments: null,
          userEmail: ticket?.userEmail,
          appOrigin: ticket?.appOrigin,
          companyName: ticket?.companyName,
          demandType: ticket?.demandType || 'SUPPORT',
          status: newStatus,
          priority: ticket?.priority,
        };
        if (resolutionPayload?.resolutionType) {
          requestPayload.resolutionType = resolutionPayload.resolutionType;
        }
        if (resolutionPayload?.resolutionNotes) {
          requestPayload.resolutionNotes = resolutionPayload.resolutionNotes;
        }
        await ticketService.updateTicketDetails(ticketId, requestPayload);
      } else {
        await ticketService.updateTicketStatus(ticketId, newStatus);
      }
      addToast(
        `Status atualizado para "${getStatusLabel(newStatus) || newStatus}"`,
        'success'
      );
    } catch (err) {
      const revertedStatus = previousStatus;
      setKanbanTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId ? { ...t, status: revertedStatus } : t
        )
      );
      const apiMessage = err?.response?.data?.detail;
      addToast(
        apiMessage || 'Não foi possível atualizar o status do ticket.',
        'error'
      );
      throw err;
    }
  }, [addToast]);

  const value = {
    users,
    tickets,
    selectedTicket,
    ticketDetails,
    loading,
    detailsLoading,
    loadingUsers,
    error,
    detailsError,
    currentPage: currentPage + 1, // para a UI
    hasMore,
    totalElements,
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    appOrigins,
    loadingAppOrigins,
    pageSize,
    setPageSize: handlePageSizeChange,
    totalPages,
    currentStatus,
    setCurrentStatus,
    clearError: () => setError(null),
    fetchUsers,
    fetchTickets,
    fetchTicketDetails,
    handleSelectTicket,
    createTicket,
    updateTicket,
    deleteTicket,
    createUser,
    updateUser,
    deleteUser,
    currentUser,
    fetchCurrentUser,
    handleStatusChange,
    handlePriorityChange,
    handleAppOriginChange,
    searchText,
    handleSearchTextChange,
    selectedDemandType,
    viewDemandTypePreset,
    handleDemandTypeChange,
    applyViewDemandTypePreset,
    applyViewConfig,
    listViewMode,
    setListViewMode: setListViewModeAndRefresh,
    kanbanTickets,
    kanbanLoading,
    moveKanbanTicketStatus,
    currentViewKey,
    filterPresets,
    activeFilterPresetId,
    applyFilterPresetById,
    saveCurrentFilterPreset,
    makeFilterPresetDefault,
    removeFilterPreset,
    getFiltersSnapshot,
    selectedCompanyNames,
    handleCompanyNamesChange,
    companyNames,
    loadingCompanyNames,
    fetchCompanyNames,
    selectedResponsibleEmails,
    handleResponsibleEmailsChange,
    ticketStatuses,
    loadingTicketStatuses,
    reloadTicketStatuses,
    demandTypes,
    loadingDemandTypes,
    reloadDemandTypes,
    handlePageChange,
    loadMoreTickets: () => fetchTickets(false, null),
    userTickets,
    userTotalElements,
    userTotalPages,
    userCurrentPage: userCurrentPage + 1,
    userLoading,
    userError,
    userSelectedStatus,
    userSelectedPriority,
    handleUserStatusChange,
    handleUserPriorityChange,
    fetchUserTickets,
    userListViewMode,
    setUserListViewMode,
    userKanbanTickets,
    userKanbanLoading,
    fetchUserKanbanTickets,
    handleUserPageChange,
    handleUserPageSizeChange,
    totalTickets,
    reloadUsers,
    reloadAppOrigins,
    resolutionTypes,
    loadingResolutionTypes,
    reloadResolutionTypes,
    updateTicketFields, // expor no contexto
    toasts,
    addToast,
    removeToast,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppProvider;
